import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Divider, Image, Modal, Row, Spin } from 'antd';
import moment from 'moment';
import { BasicCardInfoItem, IguestInfo } from '@/redux/reducers/guest';
import { DATE_FORMAT, DATE_TIME_FORMAT, GENDER_MAP } from '@/config';
import { fetchBindApp, fetchBindAppCheck, fetchHonorList } from '@/api/guest';
import { isStandingCardTime } from '@/utils/transform';
import { renderIntlLabel } from '@/utils';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';

import GuestInfoModal, { GuestInfoModalRefProps } from './GuestInfoModal';
import styles from './index.module.scss';

interface BasicInfoProps {
  memberLevelMap?: any;
  guestInfo?: Partial<IguestInfo>;
  basicCardInfo?: BasicCardInfoItem;
  onBasicEdit?: () => void;
  cardInfoLoading?: boolean;
}

const bindMsgMap = {
  // 后端msg
  1: {
    title: renderIntlLabel('guest_msg_binding_cannot_bind'),
    content: renderIntlLabel('guest_msg_binding_content1'),
  },
  4: {
    title: renderIntlLabel('guest_msg_binding_cannot_bind'),
    content: renderIntlLabel('guest_msg_binding_content2'),
  },
  // 产品提示
  2: {
    title: renderIntlLabel('guest_msg_binding_cannot_bind'),
    content: renderIntlLabel('guest_msg_binding_content3'),
  },
  3: {
    title: renderIntlLabel('guest_msg_binding_cannot_bind'),
    content: renderIntlLabel('guest_msg_binding_content4'),
  },
  5: {
    title: renderIntlLabel('guest_msg_binding_cannot_bind'),
    content: renderIntlLabel('guest_msg_binding_content5'),
  },
  0: {
    title: renderIntlLabel('guest_msg_binding_bind_successfully'),
    content: renderIntlLabel('guest_msg_binding_content6'),
  },
};

const BasicInfo: React.FC<BasicInfoProps> = ({
  memberLevelMap,
  guestInfo,
  basicCardInfo,
  onBasicEdit,
  cardInfoLoading,
}) => {
  const [isShowBind, setIsShowBind] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [bindLoading, setBindLoading] = useState<boolean>(false);

  const [honorInfoList, setHonorInfoList] = useState<any>([]);
  const [honor, setHonor] = useState<any>({});
  const [honorOpen, setHonorOpen] = useState<boolean>(false);

  const infoModalRef = useRef<GuestInfoModalRefProps>();

  const initHonor = async () => {
    try {
      const res = await fetchHonorList({ wyuid: guestInfo?.wyuid });
      if (res.code === 0) {
        setHonorInfoList(res.data || []);
      }
    } catch (error) {
      //
    }
  };

  const checkBindBtn = async () => {
    try {
      setShowLoading(true);
      const res = await fetchBindAppCheck({ wyuid: guestInfo?.wyuid });
      if (res.code === 0) {
        // 1-是身份证号 2-手机号没有找到APP用户 4-卡号已经存在对应用户，5-用户已经绑定证件号
        setIsShowBind(res.data?.code !== 1 && res.data?.code !== 4);
      }
      setShowLoading(false);
    } catch (error) {
      setShowLoading(false);
    }
  };

  const handleBindApp = async () => {
    if (!guestInfo?.mobile) {
      setTitle(renderIntlLabel('order_roomorder_placeholder_input_mobile'));
      setContent(renderIntlLabel('guest_msg_binding_content7'));
      setOpen(true);
      return;
    }
    try {
      setBindLoading(true);
      const res = await fetchBindApp({ wyuid: guestInfo?.wyuid });
      if (res.code === 0) {
        setTitle(bindMsgMap[res.data?.code]?.title);
        setContent(bindMsgMap[res.data?.code]?.content);
        setOpen(true);
      }
      setBindLoading(false);
    } catch (error) {
      setBindLoading(false);
    }
  };

  const handleOpenEdit = () => {
    infoModalRef.current?.openModal({ guestInfo });
  };

  const onEditModalSuccess = () => {
    if (typeof onBasicEdit === 'function') {
      onBasicEdit();
    }
  };

  const handleOpenHonorModal = (item) => {
    setHonor(item);
    setHonorOpen(true);
  };

  const renderCardTimeText = (val) => {
    if (isStandingCardTime(val)) return renderIntlLabel('device_long_term');
    const itemVal = moment(val ?? null);
    if (itemVal.isValid()) {
      return itemVal.format(DATE_FORMAT);
    }
    return '';
  };

  useEffect(() => {
    if (guestInfo?.wyuid) {
      checkBindBtn();
      initHonor();
    }
  }, [guestInfo]);

  return (
    <>
      <div className={styles.basicContainer}>
        <Spin spinning={cardInfoLoading}>
          <div className={styles.actionBtns}>
            <Spin spinning={showLoading}>
              {isShowBind && (
                <Button
                  type="primary"
                  onClick={handleBindApp}
                  loading={bindLoading}
                  style={{ marginRight: '16px' }}
                >
                  {renderIntlLabel('guest_action_bind_wangyu_app')}
                </Button>
              )}
            </Spin>
            <Button type="primary" onClick={handleOpenEdit}>
              {renderIntlLabel('guest_action_edit_baseinfo')}
            </Button>
          </div>
          <Row gutter={[0, 8]} style={{ marginTop: '12px' }}>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_name')}</Col>
            <Col span={18}>
              {basicCardInfo?.name || '—'}
              {(basicCardInfo?.tags || []).includes('COLLEGE_STUDENT') ? (
                <span className={styles.studentTag}>大学生</span>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_sex')}</Col>
            <Col span={18}>{GENDER_MAP[basicCardInfo?.sex] || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_nation')}</Col>
            <Col span={18}>{basicCardInfo?.nation || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_birth')}</Col>
            <Col span={18}>
              {basicCardInfo?.birthday ? moment(basicCardInfo?.birthday).format(DATE_FORMAT) : '—'}
            </Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_address')}</Col>
            <Col span={18}>{basicCardInfo?.certificateLocation || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_cert_type')}</Col>
            <Col span={18}>
              {TenantGlobalConfig.getSystemConfig().certTypesMap?.[
                basicCardInfo?.certificateType
              ] || '—'}
            </Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_roomorder_cert_no')}</Col>
            <Col span={18}>{basicCardInfo?.certNo || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>
              {renderIntlLabel('order_roomorder_cert_identity_issuing_authority')}
            </Col>
            <Col span={18}>{basicCardInfo?.issuingAuthority || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>
              {renderIntlLabel('order_roomorder_cert_identity_indate')}
            </Col>
            <Col span={18}>
              {`${renderCardTimeText(basicCardInfo?.certificateStartTime)}—${renderCardTimeText(
                basicCardInfo?.certificateEndTime,
              )}`}
            </Col>
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <Row>
            <Col className={styles.title}>
              {renderIntlLabel('guest_card_label_firstCheckinTime')}
            </Col>
            <Col span={18}>
              {basicCardInfo?.firstCheckinTime
                ? moment(basicCardInfo?.firstCheckinTime).format(DATE_TIME_FORMAT)
                : '—'}
            </Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('app_reservation_member_level')}</Col>
            <Col span={18}>{memberLevelMap[basicCardInfo?.memberLevel]?.name || '—'}</Col>
          </Row>
          <Row>
            <Col className={styles.title}>{renderIntlLabel('order_detail_guest_remark')}</Col>
            <Col span={18}>{basicCardInfo?.remark || '—'}</Col>
            <Col span={24}>
              <Divider />
            </Col>
          </Row>

          <Row>
            <Col className={styles.title}>
              {renderIntlLabel('order_detail_guest_achievement_tag')}
            </Col>
            <Col span={18}>
              {honorInfoList?.length ? (
                <div style={{ display: 'flex', flexFlow: 'wrap' }}>
                  {honorInfoList?.map((item) => (
                    <div
                      className={styles.honorItem}
                      key={item.title}
                      onClick={() => handleOpenHonorModal(item)}
                    >
                      {item?.name && <div>{item?.name}</div>}
                      {item?.title && <div>{item?.title}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </Col>
          </Row>
        </Spin>

        <Modal
          wrapClassName={styles.honorModal}
          title={renderIntlLabel('guest_title_user_achievement_detail')}
          maskClosable={false}
          open={honorOpen}
          onCancel={() => {
            setHonor({});
            setHonorOpen(false);
          }}
          footer={null}
        >
          <div style={{ textAlign: 'center', fontSize: '14px', color: '#323233' }}>
            <div style={{ marginBottom: '20px' }}>
              <Image width={150} src={honor?.icon} />
            </div>
            <div className={styles.name}>{honor?.name}</div>
            <div className={styles.title}>{honor?.title}</div>
            <div className={styles.description}>{honor?.description}</div>
            <div>{honor?.gainTime}</div>
          </div>
        </Modal>

        <Modal
          title={title}
          maskClosable={false}
          cancelButtonProps={{ hidden: true }}
          open={open}
          onCancel={() => setOpen(false)}
          onOk={() => setOpen(false)}
        >
          {content}
        </Modal>

        <GuestInfoModal ref={infoModalRef} onEditModalSuccess={onEditModalSuccess} />
      </div>
    </>
  );
};

export default BasicInfo;
